import React, { useState } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import FAQSchema from '../../FAQSchema';

// Register necessary components
ChartJS.register(ArcElement, Tooltip, Legend);

const FlatReducingRateCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(100000);
  const [interestRate, setInterestRate] = useState(10);
  const [loanTenure, setLoanTenure] = useState(12);
  const [isYears, setIsYears] = useState(true);

  const calculateFlatRate = (amount, rate, tenure) => {
    const monthlyInterest = (amount * rate) / 100 / 12;
    const monthlyEMI = (amount / tenure) + monthlyInterest;
    const totalInterest = monthlyInterest * tenure;
    const totalAmount = amount + totalInterest;

    return { monthlyEMI, totalInterest, totalAmount };
  };

  const calculateReducingBalanceRate = (amount, rate, tenure) => {
    const monthlyRate = rate / 100 / 12;
    const emi = amount * monthlyRate * (Math.pow(1 + monthlyRate, tenure) / (Math.pow(1 + monthlyRate, tenure) - 1));
    const totalAmount = emi * tenure;
    const totalInterest = totalAmount - amount;

    return { monthlyEMI: emi, totalInterest, totalAmount };
  };

  const tenureInMonths = isYears ? loanTenure * 12 : loanTenure;

  const flatRate = calculateFlatRate(loanAmount, interestRate, tenureInMonths);
  const reducingBalanceRate = calculateReducingBalanceRate(loanAmount, interestRate, tenureInMonths);

  const flatData = {
    labels: ['Principal Amount', 'Total Interest'],
    datasets: [
      {
        data: [loanAmount, flatRate.totalInterest],
        backgroundColor: ['#36A2EB', '#FF6384'],
      },
    ],
  };

  const reducingData = {
    labels: ['Principal Amount', 'Total Interest'],
    datasets: [
      {
        data: [loanAmount, reducingBalanceRate.totalInterest],
        backgroundColor: ['#36A2EB', '#FF6384'],
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: true,
    responsive: true,
  };
  function Icon({ id, open }) {
      return (
          <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
          >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
      );
    }
    
    
      const [activeIndex, setActiveIndex] = useState(0);
    
       
      const faqs = [
        {
          question: 'What is the main difference between flat and reducing interest rates?',
          answer: 'Flat interest rates calculate interest on the full loan amount throughout the tenure, while reducing rates calculate interest on the outstanding balance, which decreases with every EMI.',
        },
        {
          question: 'Which interest rate method is cheaper?',
          answer: 'Reducing balance is usually cheaper, especially for longer-term loans.',
        },
        {
          question: 'Why do banks still offer flat interest rates?',
          answer: 'Flat rates are simpler to compute and are often used for small personal or consumer loans where ease of understanding is prioritized.',
        },
        {
          question: 'How can I calculate interest under both methods?',
          answer: 'Use TheFinMaster’s Flat vs Reducing Rate Calculator by entering basic details like loan amount, interest rate, and tenure.',
        },
        {
          question: 'Can I switch from flat rate to reducing rate in a loan?',
          answer: 'Usually not within the same loan. However, you can refinance or transfer your loan to a lender offering reducing balance rates.',
        },
        {
          question: 'Is flat interest rate the same as fixed rate?',
          answer: 'No. Flat interest is a method of interest calculation. Fixed rate refers to an unchanging interest rate, which can apply to either flat or reducing models.',
        },
        {
          question: 'How much more do I pay with a flat rate loan?',
          answer: 'On average, flat rate loans can cost 20–30% more in interest compared to reducing rate loans of similar tenure and rate.',
        },
        {
          question: 'Why do some NBFCs promote flat rate loans?',
          answer: 'Because they appear lower on paper. For instance, a 10% flat rate is roughly equivalent to a 17–18% reducing balance rate.',
        },
        {
          question: 'Is flat rate suitable for home loans?',
          answer: 'No. Due to high loan amounts and long tenures, flat rate home loans are not cost-effective.',
        },
        {
          question: 'How often is interest recalculated in reducing balance loans?',
          answer: 'Interest is recalculated monthly, as EMIs reduce the principal every month.',
        },
        {
          question: 'Are EMI amounts different in both models?',
          answer: 'Yes. Flat rate EMIs stay constant, while reducing rate EMIs may start higher but save more over time due to lower interest.',
        },
        {
          question: 'How accurate is the calculator?',
          answer: 'Our calculator uses industry-standard formulas and provides highly reliable estimates to compare both interest methods.',
        },
        {
          question: 'Do reducing balance loans require more documentation?',
          answer: 'No. Documentation depends on the type of loan, not the interest calculation method.',
        },
        {
          question: 'Is prepayment easier with reducing balance loans?',
          answer: 'Yes. Since interest is calculated on the remaining principal, prepaying reduces the future interest burden.',
        },
        {
          question: 'Does the calculator work for all types of loans?',
          answer: 'Yes, it can be used for home loans, personal loans, education loans, or any loan where comparing interest methods is useful.',
        },
      ];
      const toggleAccordion = (index) => {
        if (activeIndex === index) {
            // If the clicked question is already expanded, collapse it
            setActiveIndex(null);
        } else {
            // If the clicked question is not expanded, expand it
            setActiveIndex(index);
        };
      }
  return (
    <>


      <Helmet>
         <title>Flat vs Reducing Rate Calculator – Compare Loan Interest</title>
        <meta  name='description' content='Compare flat interest and reducing balance loan rates to understand true loan costs with our calculator.'/>
        <meta name='keywords' content='flat vs reducing rate, emi calculator flat vs reducing, flat interest rate vs reducing balance calculator, flat interest rate vs reducing interest rate calculator' />
        <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/flat-vs-reducing-rate-calculator" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
      <ol className="list-none p-0 inline-flex">
        <li className="flex items-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
        <li className="flex items-center">
          <Link to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
          <li className="flex items-center">
          <span className="text-gray-500 ">Flat Reducing Rate Calculator</span>
        </li> 
      
      </ol>
    </nav>
    <div className='py-4'>

   
    <div className="container mx-auto p-4 shadow-xl rounded-md border">
      <h1 className="text-2xl font-bold mb-4">Flat vs Reducing Rate Calculator</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Loan Amount</label>
            <input
              type="number"
              min="0"
              value={loanAmount}
              onChange={(e) => setLoanAmount(Number(e.target.value))}
              className="w-full p-2 border rounded"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Rate of Interest (per annum)</label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={interestRate}
              onChange={(e) => setInterestRate(Number(e.target.value))}
              className="w-full p-2 border rounded"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Loan Tenure</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                value={loanTenure}
                onChange={(e) => setLoanTenure(Number(e.target.value))}
                className="w-full p-2 border rounded mr-2"
              />
              <div className='flex items-center '>
                <input
                  type="radio"
                  name="tenureType"
                  value="years"
                  checked={isYears}
                  onChange={() => setIsYears(true)}
                  className="mr-1"
                />
                <label className="mr-4">Years</label>
                <input
                  type="radio"
                  name="tenureType"
                  value="months"
                  checked={!isYears}
                  onChange={() => setIsYears(false)}
                  className="mr-1"
                />
                <label>Months</label>
              </div>
            </div>
          </div>
          <div className="mb-4">
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded"
              onClick={() => {
                setLoanTenure(loanTenure); // Just to trigger re-render
              }}
            >
              Calculate
            </button>
          </div>
        </div>
        <div >
<div className='flex flex-col md:flex-row justify-center gap-4'>
          <div className="mb-4">
            <h2 className="text-xl font-bold">Flat Interest Rate</h2>
            <p>Monthly EMI: ₹{flatRate.monthlyEMI.toFixed(2)}</p>
            <p>Total Interest: ₹{flatRate.totalInterest.toFixed(2)}</p>
            <p>Total Amount: ₹{flatRate.totalAmount.toFixed(2)}</p>
          </div>
          <div className="mb-4">
            <h2 className="text-xl font-bold">Reducing Balance Interest Rate</h2>
            <p>Monthly EMI: ₹{reducingBalanceRate.monthlyEMI.toFixed(2)}</p>
            <p>Total Interest: ₹{reducingBalanceRate.totalInterest.toFixed(2)}</p>
            <p>Total Amount: ₹{reducingBalanceRate.totalAmount.toFixed(2)}</p>
          </div>
          </div>
          <div className="mb-4">
            <h2 className="text-xl font-bold lg:pl-16 pl-0">Breakdown</h2>
            <div className="flex flex-col md:flex-row justify-center">
              <div className="w-full md:w-1/2 p-2">
                <h3 className="text-center font-semibold">Flat</h3>
                <div className="chart-container">
                  <Pie data={flatData} options={chartOptions} />
                </div>
              </div>
              <div className="w-full md:w-1/2 p-2">
                <h3 className="text-center font-semibold">Reducing Balance</h3>
                <div className="chart-container flex justify-center">
                  <Pie data={reducingData} options={chartOptions} width={300} height={300} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
    <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6" >
    <div className="w-full md:w-2/3 p-4 mx-auto">
    <div className="container mx-auto py-4">
          <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            What is a Flat vs Reducing Rate Calculator?
          </h1>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            When borrowing money, understanding how interest is calculated can make a significant difference in your overall repayment. One of the most critical distinctions in loan planning lies between flat interest rate and reducing balance interest rate methods. These two approaches affect the total cost of your loan and your monthly installments in very different ways. That’s where a Flat vs Reducing Rate Calculator becomes invaluable.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            The Flat vs Reducing Rate Calculator is a financial tool that helps you compare the total interest outflow and EMIs for both types of interest rate structures. It empowers you to make a data-driven decision by simulating the outcomes of your loan under each method—without needing to manually apply complex formulas. Whether you're planning to take a personal loan, car loan, business loan, or home loan, this calculator provides clear, quick, and practical insights into which option may save you more money over the long term.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            With just a few inputs like loan amount, tenure, and interest rate, the calculator will break down:
          </p>
          <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
            <li>The total interest paid for both flat and reducing methods</li>
            <li>The monthly EMI (Equated Monthly Installment) in both scenarios</li>
            <li>The overall repayment amount</li>
            <li>The cost difference between the two interest rate structures</li>
          </ul>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            By clearly showing how much you could save with one method over the other, this tool helps you become a smarter borrower.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is Loan Interest?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Loan interest is the price you pay for borrowing money from a lender. It’s essentially the profit that banks or NBFCs make for offering you financial support. Interest is usually expressed as a percentage per annum (commonly referred to as APR or Annual Percentage Rate). However, how this percentage is calculated and applied can vary significantly, influencing your monthly EMI and the total cost of the loan.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Why Does the Type of Interest Matter?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              When two loans offer the same interest rate—say 12%—you might assume the total repayment would be similar. But the calculation method makes a huge difference. A flat rate and a reducing balance rate may sound like they offer the same deal, but one can cost you significantly more over time. Understanding how each works is critical to minimizing loan costs and maximizing financial efficiency.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              EMI Breakdown: How Your Loan Is Repaid
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              When you take a loan, repayment usually happens via EMIs (Equated Monthly Installments). Each EMI contains:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Principal:</strong> This portion reduces your outstanding loan amount.</li>
              <li><strong>Interest:</strong> This is the cost charged by the lender on the remaining balance (or the full amount in case of flat rate loans).</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              As months go by, the interest component reduces and the principal component increases (in case of reducing balance loans). With flat rate loans, the structure stays the same throughout the term.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Deep Dive: What is a Flat Interest Rate?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              A Flat Interest Rate method calculates interest on the entire original loan amount for the full tenure, regardless of how much of the loan you have repaid. This makes it easier to compute, but usually results in higher overall interest costs.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Characteristics of Flat Interest Rate Loans:</strong>
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Simple to Calculate:</strong> Ideal for quick comparisons or fixed short-term obligations.</li>
              <li><strong>Fixed Monthly Repayment:</strong> Your EMI remains the same every month.</li>
              <li><strong>Less Transparent on Cost:</strong> As interest doesn't reduce with the principal, you end up paying more over time.</li>
              <li><strong>Popular for Personal or Consumer Loans:</strong> Commonly offered in auto loans, personal loans, and short-term financing schemes.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Flat Interest Rate Formula:</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Total Interest = (P × R × T) / 100
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Total Repayment = P + Interest
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Monthly EMI = Total Repayment / (T × 12)
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>P = Loan Amount</li>
              <li>R = Annual Interest Rate</li>
              <li>T = Loan Tenure in Years</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Understanding Reducing Balance Interest Rate
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              In contrast, a Reducing Balance Interest Rate (also known as diminishing or declining balance rate) calculates interest only on the remaining outstanding principal after each EMI. As you pay off the principal, your interest outgo also reduces over time.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Key Features of Reducing Rate Loans:</strong>
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Interest Decreases with Each EMI:</strong> As your loan balance shrinks, so does the interest charged.</li>
              <li><strong>Lower Total Interest Cost:</strong> You save significantly, especially on long-tenure loans.</li>
              <li><strong>Complex Calculation:</strong> Slightly more complicated but more accurate and transparent.</li>
              <li><strong>Widely Used for Home & Business Loans:</strong> Preferred in mortgages, education loans, and long-term borrowing.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Reducing Balance EMI Formula:</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              EMI = [P × r × (1 + r)^n] / [(1 + r)^n – 1]
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>P = Principal loan amount</li>
              <li>r = Monthly interest rate (annual rate ÷ 12 ÷ 100)</li>
              <li>n = Number of monthly installments</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Total Interest Paid = Total of All EMIs – Principal
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Total Repayment = EMI × n
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Comparing Flat vs Reducing Interest Rates: Which Is Better?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Choosing the right interest model depends on your financial goal, loan duration, and cash flow. Here's a quick perspective:
            </p>
            {/* <div className="overflow-x-auto mt-4">
              <table className="table-auto w-full border-collapse border border-gray-300">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="px-4 py-2 text-left">Feature</th>
                    <th className="px-4 py-2 text-left">Flat Rate</th>
                    <th className="px-4 py-2 text-left">Reducing Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-300 text-gray-600">
                  <tr>
                    <td className="px-4 py-2">EMI Amount</td>
                    <td className="px-4 py-2">Constant</td>
                    <td className="px-4 py-2">Varies slightly (lower interest over time)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Interest Basis</td>
                    <td className="px-4 py-2">Entire principal</td>
                    <td className="px-4 py-2">Outstanding principal only</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Transparency</td>
                    <td className="px-4 py-2">Lower</td>
                    <td className="px-4 py-2">Higher</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Total Interest Paid</td>
                    <td className="px-4 py-2">Higher</td>
                    <td className="px-4 py-2">Lower</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Calculation Complexity</td>
                    <td className="px-4 py-2">Simple</td>
                    <td className="px-4 py-2">Complex</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Best For</td>
                    <td className="px-4 py-2">Short-term consumer loans</td>
                    <td className="px-4 py-2">Long-term home/business loans</td>
                  </tr>
                </tbody>
              </table>
            </div> */}
            <div className="mt-4">
  <div className="hidden md:block overflow-x-auto">
    <table className="table-auto w-full border-collapse border border-gray-300">
      <thead className="bg-gray-800 text-white">
        <tr>
          <th className="px-4 py-2 text-left">Feature</th>
          <th className="px-4 py-2 text-left">Flat Rate</th>
          <th className="px-4 py-2 text-left">Reducing Rate</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-300 text-gray-600">
        <tr>
          <td className="px-4 py-2">EMI Amount</td>
          <td className="px-4 py-2">Constant</td>
          <td className="px-4 py-2">Varies slightly (lower interest over time)</td>
        </tr>
        <tr>
          <td className="px-4 py-2">Interest Basis</td>
          <td className="px-4 py-2">Entire principal</td>
          <td className="px-4 py-2">Outstanding principal only</td>
        </tr>
        <tr>
          <td className="px-4 py-2">Transparency</td>
          <td className="px-4 py-2">Lower</td>
          <td className="px-4 py-2">Higher</td>
        </tr>
        <tr>
          <td className="px-4 py-2">Total Interest Paid</td>
          <td className="px-4 py-2">Higher</td>
          <td className="px-4 py-2">Lower</td>
        </tr>
        <tr>
          <td className="px-4 py-2">Calculation Complexity</td>
          <td className="px-4 py-2">Simple</td>
          <td className="px-4 py-2">Complex</td>
        </tr>
        <tr>
          <td className="px-4 py-2">Best For</td>
          <td className="px-4 py-2">Short-term consumer loans</td>
          <td className="px-4 py-2">Long-term home/business loans</td>
        </tr>
      </tbody>
    </table>
  </div>

  {/* Mobile View (Stacked) */}
  <div className="block md:hidden space-y-4">
    {[
      {
        feature: "EMI Amount",
        flat: "Constant",
        reducing: "Varies slightly (lower interest over time)",
      },
      {
        feature: "Interest Basis",
        flat: "Entire principal",
        reducing: "Outstanding principal only",
      },
      {
        feature: "Transparency",
        flat: "Lower",
        reducing: "Higher",
      },
      {
        feature: "Total Interest Paid",
        flat: "Higher",
        reducing: "Lower",
      },
      {
        feature: "Calculation Complexity",
        flat: "Simple",
        reducing: "Complex",
      },
      {
        feature: "Best For",
        flat: "Short-term consumer loans",
        reducing: "Long-term home/business loans",
      },
    ].map((item, index) => (
      <div
        key={index}
        className="border border-gray-300 rounded-lg p-4 bg-white shadow-sm"
      >
        <p className="font-semibold text-gray-800">{item.feature}</p>
        <div className="flex justify-between text-sm text-gray-600 mt-2">
          <div>
            <span className="font-medium">Flat Rate:</span> {item.flat}
          </div>
          <div>
            <span className="font-medium">Reducing Rate:</span> {item.reducing}
          </div>
        </div>
      </div>
    ))}
  </div>
</div>

          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Why Use the Flat vs Reducing Rate Calculator?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Our loan interest calculator at TheFinMaster allows you to visualize the real cost differences between both methods:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Save Money on Interest:</strong> Compare how much more you’d pay under a flat rate compared to a reducing balance rate.</li>
              <li><strong>Quick Estimations:</strong> Just input your loan amount, interest rate, and tenure—and the calculator does the math instantly.</li>
              <li><strong>Make Informed Decisions:</strong> Get transparency in numbers before signing any loan agreement.</li>
              <li><strong>Plan Your Budget:</strong> Anticipate your monthly EMIs and plan your finances accordingly.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Use the Calculator
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using TheFinMaster's calculator is easy and free:
            </p>
            <ul className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Enter Loan Amount:</strong> The total sum you wish to borrow.</li>
              <li><strong>Input Annual Interest Rate:</strong> Provided by your lender.</li>
              <li><strong>Select Loan Tenure:</strong> In years.</li>
              <li><strong>Click Calculate:</strong> Instantly view EMI, total repayment, and interest payable for both flat and reducing methods.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Choosing the Right Interest Model: Tips for Borrowers
            </h1>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>For Predictability:</strong> Flat rates offer consistency. Ideal if you prefer fixed monthly budgeting.</li>
              <li><strong>For Long-Term Savings:</strong> Reducing balance rates reduce your interest over time.</li>
              <li><strong>For High-Value Loans:</strong> Always opt for reducing rates to save on large ticket borrowings.</li>
              <li><strong>For Short-Term Loans:</strong> The difference might be negligible, but it’s still worth comparing.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Beyond Basics: Learn & Grow with TheFinMaster
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Understanding interest rates is just the beginning. At TheFinMaster, we offer:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Loan Comparison Tools:</strong> Compare across banks and NBFCs.</li>
              <li><strong>Educational Guides:</strong> Learn about prepayments, part-payments, foreclosure, and more.</li>
              <li><strong>Personal Finance Blogs:</strong> Stay updated with the latest trends and financial advice.</li>
              <li><strong>Mutual Fund & SIP Tools:</strong> Grow your wealth smartly while managing liabilities.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Start Smart, Borrow Smart
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Your borrowing decision can make or break your financial health. TheFinMaster empowers you with the right tools and knowledge to make smarter financial moves.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>👉 Use the Flat vs Reducing Rate Calculator Today!</strong>
            </p>
          </div>
        </div>

        <div className="w-full md:w-1/3 p-4">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
              <h2 className="text-lg font-semibold ">Popular Categories</h2>
            </div>
            <div className="p-2 ">
            <ul>
                                <Link to='/financial-tools-calculators/swp-calculator'>
                                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">SWP Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/mutul-fund-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Mutual Fund Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/sukanya-samriddhi-yojana-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Samriddhi Yojana Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/fd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">FD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/rd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">RD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/nps-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NPS Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/hra-exemption-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">HRA Exemption Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/retirement-planning-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Retirement Planning Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">EMI Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/simple-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Simple Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/compound-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Compound Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/nsc-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NSC Calculator</li>

                                </Link><Link to='/financial-tools-calculators/step-up-sip-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Step Up Sip Calculator</li>

                                </Link><Link to='/financial-tools-calculators/income-tax-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Income Tax Calculator</li>
                                    
                                </Link><Link to='/financial-tools-calculators/home-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Home Loan Emi Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/car-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Car Loan Emi Calculator</li>
                                </Link>
                                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                            </ul>
                        </div>
                    </div>
                    <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
                        <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                            <h2 className="text-lg font-semibold">More Categories</h2>
                        </div>
                        <div className="p-2">
                        <ul>
                                <Link to='/financial-tools-calculators/gratuity-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Gratuity Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/APY-calculator'>
                                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">APY Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/cagr-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">CAGR Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/gst-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">GST Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/flat-vs-reducing-rate-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Flat Vs Reducing Rate Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/equity-margin-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Equity Margin Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/inflation-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Inflation Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/salary-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Salary Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/brokerage-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Brokerage Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/tds-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">TDS Calculator</li>
                                </Link>
                {/* {moreCategories.map(category => (
          <Link key={category.id} to={`/category/${category.id}`}>
            <li className="py-2 border-b border-gray-300">{category.name}</li>
          </Link>
        ))} */}
              </ul>
            </div>
          </div>

        </div>

      </div>

      <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
                <h1 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h1>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>


    </>
  );
};

export default FlatReducingRateCalculator;
